import React, { useEffect, useMemo, useState } from 'react';
import * as Styles from './UpgradeListing.styles';

import useLocale from '@/hooks/useLocale';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import {
  IconCheck, Button,
} from 'gatorcomponents';

import { useResize } from '@/hooks/useResize';
import upgradeSnappyImage from './media/upgrade-plan-snappy.png';

const processPlanData = (plan, currentCycle) => {
  const resources = plan.resources || {};
  const { processor, disk, memory } = resources;

  const resourcesList = [
    { value: processor, testId: 'processor' },
    { value: memory, testId: 'memory' },
    { value: disk, testId: 'disk' },
  ].filter(resource => resource.value != null);

  const price = plan && plan.prices && plan.prices.find(p => p.billingCycle === currentCycle);
  const { billingCycle, fullPrice, monthlyPrice } = price || {};

  return {
    resourcesList,
    billingCycle,
    fullPrice,
    monthlyPrice,
  };
};

const UpgradeListing = ({
  onSelectPlan,
}) => {
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;
  const isNotebook = width >= 960 && width < 1440;

  const dispatch = useDispatch();
  const { id: hostingId } = useParams();

  const vpsOci = useSelector(state => state.vpsOci);

  const { data: upgradeData } = (vpsOci && vpsOci.upgrade && vpsOci.upgrade.listing) || {};
  const { availables: availablePlans, currentCycle } = upgradeData || {};

  const { loading: upgradeDetailsLoading } = (vpsOci && vpsOci.upgrade && vpsOci.upgrade.details) || {};

  const hasAvailablePlans = availablePlans && availablePlans.length > 0;

  useEffect(() => {
    if (!upgradeDetailsLoading) {
      setLoadingPlanId(null);
    }
  }, [upgradeDetailsLoading]);

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;

  const upgradeLocale = vpsOciLocale && vpsOciLocale.planDetails && vpsOciLocale.planDetails.upgrade;

  const {
    benefits: {
      title: benefitsTitleLocale,
      description: benefitsDescriptionLocale,
      resources: {
        title: benefitsResourcesTitleLocale,
        items: benefitsResourcesItemsLocale,
      },
    } = {},
    planList: {
      prices: {
        separatorText: priceSeparatorTextLocale,
        suffixText: suffixPriceTextLocale,
      } = {},
      note: planListsNoteLocale,
      button: planListButtonLabelLocale,
      perCycle: perCycleLocale,
    } = {},
  } = (upgradeLocale && upgradeLocale.listing) || {};

  const resourcesSuffixLocale = (upgradeLocale && upgradeLocale.resources && upgradeLocale.resources.suffix) || '';

  const handlePlanClick = (plan) => {
    setLoadingPlanId(plan.productId);
    onSelectPlan && onSelectPlan(plan);
  };

  const getButtonState = (planId) => {
    const isThisPlan = loadingPlanId === planId;

    return {
      loading: upgradeDetailsLoading && isThisPlan,
      disabled: upgradeDetailsLoading && !isThisPlan,
    };
  };

  const processedPlans = useMemo(() => {
    if (!availablePlans) return [];
    return availablePlans.map(plan => ({
      ...plan,
      ...processPlanData(plan, currentCycle),
    }));
  }, [availablePlans, currentCycle]);

  useEffect(() => {
    if (hostingId && !upgradeData) {
      dispatch(vpsOciActions.upgrade.listing.request(hostingId));
    }
  }, [dispatch, hostingId, upgradeData]);

  if (!upgradeData || upgradeData.length === 0) return null;

  return (
    <Styles.UpgradeListing data-testid="vpsoci-upgrade-listing">
      <Styles.BenefitsWrapper data-testid="vpsoci-upgrade-listing-benefits-wrapper">
        <Styles.BenefitsContent data-testid="vpsoci-upgrade-listing-benefits-content">
          <Styles.BenefitsTitle data-testid="vpsoci-upgrade-listing-benefits-title">{benefitsTitleLocale}</Styles.BenefitsTitle>
          <Styles.BenefitsDescription data-testid="vpsoci-upgrade-listing-benefits-description">{benefitsDescriptionLocale}</Styles.BenefitsDescription>
          <Styles.BenefitsResources data-testid="vpsoci-upgrade-listing-resource">
            <Styles.BenefitsResourcesTitle data-testid="vpsoci-upgrade-listing-resource-title">{benefitsResourcesTitleLocale}</Styles.BenefitsResourcesTitle>
            <Styles.BenefitsResourcesItems data-testid="vpsoci-upgrade-listing-resource-items">
              {benefitsResourcesItemsLocale && benefitsResourcesItemsLocale.map(resource => (
                <Styles.BenefitsResourcesItem key={resource} data-testid="vpsoci-upgrade-listing-resource-item">
                  <IconCheck
                    size="sm"
                    color="primaryMedium"
                    testId="vpsoci-upgrade-listing-resource-item-icon"
                  />
                  {' '}
                  {resource}
                </Styles.BenefitsResourcesItem>
              ))}
            </Styles.BenefitsResourcesItems>
          </Styles.BenefitsResources>
        </Styles.BenefitsContent>
        {!isMobile && (
          <Styles.BenefitsSnappy data-testid="vpsoci-upgrade-listing-benefits-snappy">
            <Styles.BenefitsSnappyImage src={upgradeSnappyImage} />
          </Styles.BenefitsSnappy>
        )}
      </Styles.BenefitsWrapper>

      {hasAvailablePlans && (
        <Styles.PlanListsWrapper data-testid="vpsoci-upgrade-listing-plan-lists-wrapper">
          <Styles.PlanListsContent data-testid="vpsoci-upgrade-listing-plan-lists-content">
            {processedPlans.map(plan => (
              <Styles.PlanListItem key={plan.productId} data-testid="vpsoci-upgrade-listing-plan-list-item">

                <Styles.PlanListItemName data-testid="vpsoci-upgrade-listing-plan-list-item-name">
                  {plan.productName}
                </Styles.PlanListItemName>

                <Styles.PlanListItemDetails data-testid="vpsoci-upgrade-listing-plan-list-item-details">

                  <Styles.PlanListItemDescription data-testid="vpsoci-upgrade-listing-plan-list-item-description">

                    {plan.resourcesList.length > 0 && (
                      <Styles.PlanListItemResourcesItems data-testid="vpsoci-upgrade-listing-plan-list-item-resources-items">
                        {plan.resourcesList.map(resource => (
                          <Styles.PlanListItemResourcesItem key={resource.testId} data-testid="vpsoci-upgrade-listing-plan-list-item-resources-item">
                            <IconCheck
                              key={resource.testId}
                              size="sm"
                              color="primaryMedium"
                              testId={`vpsoci-upgrade-listing-plan-list-item-resources-${resource.testId}-icon`}
                            />
                            <Styles.PlanListItemResourcesItemValueWrapper>
                              <Styles.PlanListItemResourcesItemValue>
                                {resource.value}
                              </Styles.PlanListItemResourcesItemValue>
                              {' '}
                              <Styles.PlanListItemResourcesItemValueSuffix>
                                {resourcesSuffixLocale[resource.testId]}
                              </Styles.PlanListItemResourcesItemValueSuffix>
                            </Styles.PlanListItemResourcesItemValueWrapper>
                          </Styles.PlanListItemResourcesItem>
                        ))}
                      </Styles.PlanListItemResourcesItems>
                    )}

                    {(plan.fullPrice && plan.monthlyPrice) && (
                      <Styles.PlanListItemPrices data-testid="vpsoci-upgrade-listing-plan-list-item-prices">

                        {plan.fullPrice && (
                          <Styles.PlanListItemFullPriceWrapper>
                            <Styles.PlanListItemFullPrice data-testid="vpsoci-upgrade-listing-plan-list-item-full-price">
                              {formatCurrency(plan.fullPrice)}
                              {' '}
                              {plan.billingCycle && (
                              <Styles.PlanListItemFullPriceBillingCycle>
                                {perCycleLocale[plan.billingCycle]}
                              </Styles.PlanListItemFullPriceBillingCycle>
                              )}
                            </Styles.PlanListItemFullPrice>
                            <Styles.PlanListItemTextSeparator>{priceSeparatorTextLocale}</Styles.PlanListItemTextSeparator>
                          </Styles.PlanListItemFullPriceWrapper>
                        )}

                        {plan.monthlyPrice && (
                        <Styles.PlanListItemMonthlyPrice data-testid="vpsoci-upgrade-listing-plan-list-item-monthly-price">
                          {formatCurrency(plan.monthlyPrice)}
                          {' '}
                          <Styles.PlanListItemMonthlyPriceSuffix data-testid="vpsoci-upgrade-listing-plan-list-item-monthly-price-suffix">
                            {suffixPriceTextLocale}
                          </Styles.PlanListItemMonthlyPriceSuffix>
                        </Styles.PlanListItemMonthlyPrice>
                        )}

                      </Styles.PlanListItemPrices>
                    )}
                  </Styles.PlanListItemDescription>

                  <Styles.PlanListItemButtonWrapper data-testid="vpsoci-upgrade-listing-plan-list-item-button-wrapper">
                    <Button
                      variant="primary"
                      size={isNotebook ? 'medium' : 'large'}
                      width="full"
                      onClick={() => handlePlanClick(plan)}
                      data-testid="vpsoci-upgrade-listing-plan-list-item-button"
                      label={planListButtonLabelLocale}
                      {...getButtonState(plan.productId)}
                    />
                  </Styles.PlanListItemButtonWrapper>
                </Styles.PlanListItemDetails>
              </Styles.PlanListItem>
            ))}
          </Styles.PlanListsContent>
          <Styles.PlanListsNote data-testid="vpsoci-upgrade-listing-plan-lists-note">
            {planListsNoteLocale}
          </Styles.PlanListsNote>
        </Styles.PlanListsWrapper>
      )}
    </Styles.UpgradeListing>
  );
};

export default UpgradeListing;
